import './index.scss';
import Header from '../../../../../../../components/Header/Header';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import QuickActions from '../../../../../../../components/QuickActions';
import { copyToClipboard, formatToNaira } from '../../../../../../../helpers/utils';
import { IoCopyOutline } from 'react-icons/io5';
import useInvestify from '../../../../../../../hooks/useInvestify';
import { Loader } from '@piggytechng/component-library';
import NoData from '../../../../../../../components/NoData/NoData';
import dayjs from 'dayjs';

const index = () => {
  const params = useParams();
  const investmentId = params.investmentId as string;
  const userId = params.id as string;
  const activeTab = params.activeTab as string;

  const [formattedInvestmentData, setFormattedInvestmentData] = useState<any>(null);
  const [activeOfferTab, setActiveOfferTab] = useState<'buy' | 'sell' | 'history'>('buy');

  const {
    InvestmentData,
    isLoadingInvestmentData: isLoading,
    InvestmentOffers,
    isLoadingOffers,
    InvestmentHistory,
    isLoadingHistory,
    isLoadingMoreHistory,
    historyHasMore,
    loadMoreHistory,
  } = useInvestify({
    investmentId,
    offerListType: activeOfferTab === 'history' ? undefined : activeOfferTab,
    enableHistory: activeOfferTab === 'history',
  });

  useEffect(() => {
    if (InvestmentData) {
      const formatted: any = {
        Total: formatToNaira(InvestmentData.total),
        Capital: formatToNaira(InvestmentData.capital),
        'Asset Value': formatToNaira(InvestmentData.assetValue),
        Profit: formatToNaira(InvestmentData.profit),
        Units: InvestmentData.units.toString(),
        Date: dayjs(InvestmentData.date * 1000).format('D MMM YYYY | h:mm A'),
      };

      if (InvestmentData.status !== undefined && InvestmentData.status !== null) {
        formatted.Status = InvestmentData.status === 0 ? 'Active' : 'Matured';
      }

      setFormattedInvestmentData(formatted);
    }
  }, [InvestmentData]);

  return (
    <div className="single-investment-page">
      <Header name="Investify" backButton />

      <div className="content">
        {isLoading ? (
          <div className="page-loader">
            <Loader color="#0F60D6" />
          </div>
        ) : (
          <>
            <div className="investment-header">
              <div className="left-section">
                <h1>{InvestmentData?.title || ''}</h1>
                <div className="id-container">
                  <span className="flex--2 cont">
                    <p>Investment ID: {investmentId} </p>
                    <IoCopyOutline
                      size={18}
                      className="pointer"
                      onClick={() => copyToClipboard('Investment ID', investmentId)}
                    />{' '}
                  </span>
                </div>
              </div>
              <div className="right-section">
                <QuickActions
                  activeTabWallet="investify"
                  userId={userId}
                  targetId={investmentId}
                  activeInvestmentTab={activeTab}
                />
              </div>
            </div>

            {formattedInvestmentData && (
              <div className="investment-data">
                {Object.entries(formattedInvestmentData).map(([key, value]) => {
                  return (
                    <div className="data-box" key={key}>
                      <p>{key}</p>
                      <h3>{value as string}</h3>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="tab-switch flex--2">
              <p
                className={`pointer tab ${activeOfferTab === 'buy' && 'active'}`}
                onClick={() => setActiveOfferTab('buy')}
              >
                Buy Offers
              </p>
              <p
                className={`pointer tab ${activeOfferTab === 'sell' && 'active'}`}
                onClick={() => setActiveOfferTab('sell')}
              >
                Sell Offers
              </p>
              <p
                className={`pointer tab ${activeOfferTab === 'history' && 'active'}`}
                onClick={() => setActiveOfferTab('history')}
              >
                History
              </p>
            </div>

            {activeOfferTab === 'history' ? (
              isLoadingHistory ? (
                <div className="page-loader">
                  <Loader color="#0F60D6" />
                </div>
              ) : InvestmentHistory.length > 0 ? (
                <>
                  <div className="table-container single-investment">
                    <table className="table">
                      <thead className="table_head">
                        <tr>
                          <th className="date">Date</th>
                          <th className="title">Title</th>
                          <th className="amount">Amount (₦)</th>
                          <th className="units">Units</th>
                          <th className="reference">Reference</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {InvestmentHistory.map((row) => (
                          <tr key={row.id} className="table-row">
                            <td className="date">
                              {row.date ? dayjs(row.date * 1000).format('D MMM YYYY | h:mm A') : '-----'}
                            </td>
                            <td className="title">{row.title}</td>
                            <td className="amount">{formatToNaira(row.amount)}</td>
                            <td className="units">{row.units ?? '-----'}</td>
                            <td className="reference">{row.reference ?? '-----'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {historyHasMore && (
                    <div className="load-more" onClick={() => loadMoreHistory()}>
                      {isLoadingMoreHistory ? (
                        <Loader color="#0F60D6" maxWidth="50px" />
                      ) : (
                        <span className="pointer">Show more</span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <NoData message={'No history at the moment.'} />
              )
            ) : isLoadingOffers ? (
              <div className="page-loader">
                <Loader color="#0F60D6" />
              </div>
            ) : (
              <>
                {InvestmentOffers && InvestmentOffers.length > 0 ? (
                  <div className="table-container single-investment">
                    <table className="table">
                      <thead className="table_head">
                        <tr>
                          <th className="reference">Reference</th>
                          <th className="userId">User ID</th>
                          <th className="amount">Amount (₦)</th>
                          <th className="units">Units</th>
                          <th className="expTime">Expiry Time</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {InvestmentOffers.map((offer: any, index: number) => (
                          <tr key={index} className="table-row">
                            <td className="reference">{offer.reference}</td>
                            <td className="userId">{offer.userId}</td>
                            <td className="amount">{formatToNaira(offer.amount)}</td>
                            <td className="units">{offer.units}</td>
                            <td className="expTime">{dayjs(offer.expTime * 1000).format('D MMM YYYY | h:mm A')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <NoData message={'No offers at the moment.'} />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default index;
